import type { Session } from '../client';
import {
  GroupAdminChangedAction,
  GroupMemberOperationType,
  SessionCategoryPrefix,
  type GroupInfo,
  type User,
} from '../sdk';
import { isSessionCategory } from '../utils';
import type {
  DisallowSendCategory,
  GroupDisallowSendInfo,
  GroupMyRole,
  GroupOptionOriginal,
  GroupOptions,
  GroupPosition,
  GroupMemberJoinMode,
  CreateGroupSessionResponse,
  GroupMemberInfo,
  MyAxiosResponseData,
} from '../sdk';
import { GroupMemberRole } from '../sdk';
import { AbstractApi } from './Api';
import type { UploadAvatarPreSignBody } from './common';

export class SessionApi extends AbstractApi {
  async updateSessions(with_snapshot = true, abortSignal?: AbortSignal) {
    return await this.client.invoke('updateSessions', [with_snapshot], abortSignal);
  }
  createGroupChat(
    group_name: string,
    avatar: string,
    description: string,
    invite_member_list: string[],
    group_id_list: string[]
  ) {
    return this.client.invoke('createGroup', [
      group_name,
      avatar,
      description,
      invite_member_list,
      group_id_list,
    ]) as unknown as Promise<CreateGroupSessionResponse>;
  }
  createSingleChat(userId: string, abortSignal?: AbortSignal) {
    return this.client.invoke('createSingleChat', [userId], abortSignal);
  }
  setSessionExpireDuration(chat_id: string, expire_duration: bigint, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'setSessionExpireDuration',
      [chat_id, expire_duration],
      abortSignal
    );
  }
  inviteGroupMembers(gid: string, uids: string[], abortSignal?: AbortSignal) {
    return this.client.invoke('inviteGroupMembers', [gid, uids], abortSignal);
  }
  removeGroupMembers(gid: string, uids: string[], abortSignal?: AbortSignal) {
    return this.client.invoke('removeGroupMembers', [gid, uids], abortSignal);
  }
  disbandGroup(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke('disbandGroup', [gid], abortSignal) as unknown as Promise<any>;
  }
  quitGroup(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke('quitGroup', [gid], abortSignal) as unknown as Promise<{
      quit_fail_list: string[];
    }>;
  }
  fetchGroupMembers(params: {
    gid: string;
    // TODO amo
    // cb: (members: Array<GroupMemberInfo & Partial<User>>) => void;
    cb: (members: Array<GroupMemberInfo & User>) => void;
    role?: GroupMemberRole;
    abortSignal?: AbortSignal;
  }) {
    const { gid, role, cb, abortSignal } = params;
    const { client } = this;

    let members: GroupMemberInfo[] = [];

    const promise = client.invoke('fetchGroupMembers', [gid, true], abortSignal);
    let userInfoRemoveListener: () => void | undefined;

    function cb_(members: GroupMemberInfo[]) {
      const membersWithRole =
        role === undefined ? members : members.filter((member) => member.role === role);
      // cb(membersWithRole);

      userInfoRemoveListener?.();

      ({ removeListener: userInfoRemoveListener } = client.Api.user.resolveUsersInfo(
        members.map((m) => m.user_id),
        (userInfos) => {
          const membersWithUserInfo = membersWithRole.map((member) => {
            const userInfo = userInfos.find((userInfo) => userInfo.user_id === member.user_id);
            return { ...member, ...userInfo };
          });
          cb(membersWithUserInfo);
        }
      ));
    }

    const effect = [
      client.addEventListener(
        'GroupMembersChanged',
        async ({ detail: { group_id, increment } }) => {
          if (group_id === gid) {
            increment.forEach((member) => {
              const index = members.findIndex((m) => m.user_id === member.user_id);
              const hasMember = index !== -1;
              switch (member.operation_type) {
                case GroupMemberOperationType.ADD:
                  if (!hasMember) {
                    members = [...members, member];
                  }
                  break;
                case GroupMemberOperationType.UPDATE:
                  if (hasMember) {
                    const oldMember = members[index];
                    members = [...members];
                    if (member.role === GroupMemberRole.OWNER) {
                      client.dispatchEvent('GroupOwnerChanged', {
                        group_id: gid,
                        operator_user_id: '', // TODO amo
                        new_owner_id: member.user_id,
                      });
                    }
                    members.splice(index, 1, { ...oldMember, ...member });
                  }

                  break;
                case GroupMemberOperationType.DELETE:
                  if (hasMember) {
                    members = [...members];
                    members.splice(index, 1);
                  }
                  break;
              }
            });
            cb_(members);
          }
        }
      ),
      client.addEventListener(
        'GroupOwnerChanged',
        async ({ detail: { group_id, new_owner_id } }) => {
          if (group_id === gid) {
            const ownerIndex = members.findIndex((m) => m.role === GroupMemberRole.OWNER);
            if (ownerIndex !== -1) {
              members = [...members];
              members[ownerIndex] = { ...members[ownerIndex], role: GroupMemberRole.MEMBER };
            }

            const index = members.findIndex((m) => m.user_id === new_owner_id);
            if (index !== -1) {
              members = [...members];
              members[index] = { ...members[index], role: GroupMemberRole.OWNER };
            }
            cb_(members);
          }
        }
      ),
      client.addEventListener(
        'GroupAdminChanged',
        ({ detail: { group_id, action, user_ids } }) => {
          if (group_id === gid) {
            user_ids.forEach((user_id) => {
              const index = members.findIndex((m) => m.user_id === user_id);
              if (index !== -1) {
                members = [...members];
                members[index] = {
                  ...members[index],
                  role:
                    action === GroupAdminChangedAction.Add
                      ? GroupMemberRole.ADMIN
                      : GroupMemberRole.MEMBER,
                };
              }
            });
            cb_(members);
          }
        }
      ),
    ];

    promise.then((res) => {
      members = res;
      cb_(members);
    });

    return {
      promise,
      removeEventListener: () => {
        userInfoRemoveListener?.();
        effect.forEach((r) => r());
      },
    };
  }
  getGroupMembers(params: { gid: string; role?: GroupMemberRole; abortSignal?: AbortSignal }) {
    const { gid, role, abortSignal } = params;
    return new Promise<Array<GroupMemberInfo & User>>((resolve, reject) => {
      const { promise, removeEventListener } = this.fetchGroupMembers({
        gid,
        role,
        cb: (members) => {
          resolve(members);
          removeEventListener();
        },
        abortSignal,
      });
      promise.catch(reject);
    });
  }
  async uploadAvatar({ file, ...rest }: { file: File } & UploadAvatarPreSignBody) {
    const access_url = await this.client.Api.http.getUploadAvatar({
      file,
      ...rest,
    });
    return access_url;
  }
  getGroupInfo(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'getGroupInfo',
      [gid, true],
      abortSignal
    ) as unknown as Promise<GroupInfo>;
  }
  getGroupInfoAwait(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'getGroupInfoAwait',
      [gid],
      abortSignal
    ) as unknown as Promise<GroupInfo>;
  }
  setGroupInfo(gid: string, name: string, avatar: string, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'setGroupInfo',
      [gid, name, avatar],
      abortSignal
    ) as unknown as Promise<void>;
  }
  getGroupOptions(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getGroupOptions', [gid, true], abortSignal) as unknown as Promise<
      GroupOptionOriginal[]
    >;
  }
  setGroupOptions(gid: string, options: Partial<GroupOptions>, abortSignal?: AbortSignal) {
    return this.client.invoke('setGroupOptions', [gid, options], abortSignal);
  }
  getGroupJoins(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getGroupJoins', [gid], abortSignal) as unknown as Promise<
      string[]
    >;
  }
  groupJoinsAccept(gid: string, uids: string[], abortSignal?: AbortSignal) {
    return this.client.invoke(
      'groupJoinsAccept',
      [gid, uids],
      abortSignal
    ) as unknown as Promise<{
      not_allow_list: string[];
    }>;
  }
  groupJoinsReject(gid: string, uids: string[], abortSignal?: AbortSignal) {
    return this.client.invoke(
      'groupJoinsReject',
      [gid, uids],
      abortSignal
    ) as unknown as Promise<{
      not_allow_list: string[];
      existing_list: string[];
    }>;
  }
  transferGroupOwnership(gid: string, new_owner_user_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'transferGroupOwnership',
      [gid, new_owner_user_id],
      abortSignal
    ) as unknown as Promise<void>;
  }
  updateGroupMemberRole(
    gid: string,
    user_ids: string[],
    role: GroupMemberRole,
    abortSignal?: AbortSignal
  ) {
    return this.client.invoke(
      'updateGroupMemberRole',
      [gid, user_ids, role],
      abortSignal
    ) as unknown as Promise<{ update_fail_list: string[] }>;
  }
  getGroupPosition(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getGroupPosition', [gid], abortSignal) as unknown as Promise<
      GroupPosition[]
    >;
  }
  /** await data when force_remote is true */
  getMyGroupRole(gid: string, force_remote = true, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'getMyGroupRole',
      [gid, force_remote],
      abortSignal
    ) as unknown as Promise<GroupMyRole>;
  }
  updateGroupPosition(gid: string, position: number, name: string, abortSignal?: AbortSignal) {
    return this.client.invoke('updateGroupPosition', [gid, position, name], abortSignal);
  }
  setGroupDescription(gid: string, description: string, abortSignal?: AbortSignal) {
    return this.client.invoke('setGroupDescription', [gid, description], abortSignal);
  }
  getDisallowSendInfo(gid: string, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'getDisallowSendInfo',
      [gid],
      abortSignal
    ) as unknown as Promise<GroupDisallowSendInfo>;
  }
  disallowSend(
    gid: string,
    category: DisallowSendCategory,
    uids: string[],
    time: bigint,
    abortSignal?: AbortSignal
  ) {
    return this.client.invoke(
      'disallowSend',
      [gid, category, uids, time],
      abortSignal
    ) as unknown as Promise<void>;
  }
  hideSessions(chat_ids: string[], is_delete: boolean, abortSignal?: AbortSignal) {
    return this.client.invoke('hideSessions', [chat_ids, is_delete], abortSignal);
  }
  getSessionById(chat_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getSessionById', [chat_id], abortSignal);
  }
  setSessionCollapse(chat_id: string, collapse_group_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('setSessionCollapse', [chat_id, collapse_group_id], abortSignal);
  }
  setSessionMuted(chat_id: string, muted: boolean, abortSignal?: AbortSignal) {
    return this.client.invoke('setSessionMuted', [chat_id, muted], abortSignal);
  }
  setSessionAtAllMuted(chat_id: string, muted: boolean, abortSignal?: AbortSignal) {
    return this.client.invoke('setSessionAtAllMuted', [chat_id, muted], abortSignal);
  }
  sessionSearchByKey(key: string, exclude_contacts: boolean, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'sessionSearchByKey',
      [key, exclude_contacts],
      abortSignal
    ) as unknown as Promise<User[]>;
  }
  async joinSession(
    chat_id_like: string,
    join_mode: GroupMemberJoinMode,
    abortSignal?: AbortSignal
  ) {
    const result = {
      isSingle: isSessionCategory(chat_id_like, SessionCategoryPrefix.Single),
      chat_id: chat_id_like,
      session: undefined as Session | undefined,
      status: -1,
    };

    if (result.isSingle) {
      const userId = chat_id_like.replace(SessionCategoryPrefix.Single, '');
      result.session = await this.createSingleChat(userId, abortSignal);
      result.chat_id = result.session.chat_id;
    } else {
      const { gid, result: status } = (await this.client.invoke(
        'joinGroup',
        [chat_id_like, join_mode],
        abortSignal
      )) as unknown as { gid: string; result: number };
      result.status = status;
      result.chat_id = gid;
    }

    return result;
  }
  setSessionPinned(chat_id: string, is_pinned: boolean, abortSignal?: AbortSignal) {
    return this.client.invoke('setSessionPinned', [chat_id, is_pinned], abortSignal);
  }
  getPinnedSessionChatIds(force_remote = false, abortSignal?: AbortSignal) {
    return this.client.invoke(
      'getPinnedSessionChatIds',
      [force_remote],
      abortSignal
    ) as unknown as Promise<string[]>;
  }
  setPinnedSessionsOrder(chat_ids: string[], abortSignal?: AbortSignal) {
    return this.client.invoke('setPinnedSessionsOrder', [chat_ids], abortSignal);
  }

  async setSessionReadMark(chat_id: string, unread_mark: boolean) {
    return await this.client.invoke('setSessionUnreadMark', [chat_id, unread_mark]);
  }

  async setSessionAllRead(chat_id: string) {
    const result = await this.client.invoke('setSessionAllRead', [chat_id]);
    this.client.dispatchEvent('UpdatePartialSession', {
      chat_id,
      unread_count: 0,
    });
    return result;
  }

  getChatStatus(body: { user_ids: string[] }) {
    return this.request<MyAxiosResponseData<{ media_call: string[] }>>({
      url: '/api/v2/chat/status',
      method: 'POST',
      body,
    });
  }
}
